import { z } from "zod";
import { VALIDATION } from "./validationConstants";

// Common field validations
const emailSchema = z
  .string()
  .trim()
  .min(1, { message: "Email is required" })
  .email({ message: "Invalid email address" })
  .max(VALIDATION.EMAIL_MAX, { message: `Email must be less than ${VALIDATION.EMAIL_MAX} characters` });

const nameSchema = z
  .string()
  .trim()
  .min(1, { message: "Name is required" })
  .max(VALIDATION.CUSTOMER_NAME_MAX, { message: `Name must be less than ${VALIDATION.CUSTOMER_NAME_MAX} characters` });

const phoneSchema = z
  .string()
  .trim()
  .regex(/^[0-9+\-\s()]*$/, { message: "Invalid phone format" })
  .max(VALIDATION.PHONE_MAX, { message: `Phone must be less than ${VALIDATION.PHONE_MAX} characters` })
  .optional()
  .or(z.literal(""));

const companyNameSchema = z
  .string()
  .trim()
  .min(1, { message: "Company name is required" })
  .max(VALIDATION.COMPANY_NAME_MAX, { message: `Company name must be less than ${VALIDATION.COMPANY_NAME_MAX} characters` });

const messageSchema = z
  .string()
  .trim()
  .max(VALIDATION.MESSAGE_MAX, { message: `Message must be less than ${VALIDATION.MESSAGE_MAX} characters` })
  .optional()
  .or(z.literal(""));

// Password schema for registration
export const passwordSchema = z
  .string()
  .min(8, { message: "Password must be at least 8 characters" })
  .max(72, { message: "Password must be less than 72 characters" })
  .regex(/^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)/, {
    message: "Password must contain at least one uppercase letter, one lowercase letter, and one digit",
  });

// Manual order form schema
export const manualOrderSchema = z.object({
  orderNumber: z.string().trim().max(VALIDATION.ORDER_NUMBER_MAX, { message: `Order number must be less than ${VALIDATION.ORDER_NUMBER_MAX} characters` }).optional().or(z.literal("")),
  customerName: nameSchema,
  customerPhone: phoneSchema,
  address: z.string().trim().max(VALIDATION.ADDRESS_MAX, { message: `Address must be less than ${VALIDATION.ADDRESS_MAX} characters` }).optional().or(z.literal("")),
  message: z.string().trim().max(VALIDATION.COMMENTS_MAX, { message: `Message must be less than ${VALIDATION.COMMENTS_MAX} characters` }).optional().or(z.literal("")),
  source: z.string().min(1, { message: "Source is required" }),
  category: z.string().min(1, { message: "Category is required" }),
  totalPrice: z
    .string()
    .refine((val) => val === "" || !isNaN(parseFloat(val)), { message: "Invalid price" })
    .refine((val) => val === "" || parseFloat(val) >= 0, { message: "Price cannot be negative" })
    .optional()
    .or(z.literal("")),
  comments: z.string().trim().max(VALIDATION.COMMENTS_MAX, { message: `Comments must be less than ${VALIDATION.COMMENTS_MAX} characters` }).optional().or(z.literal("")),
  csrName: z.string().trim().max(VALIDATION.CSR_NAME_MAX, { message: `CSR name must be less than ${VALIDATION.CSR_NAME_MAX} characters` }).optional().or(z.literal("")),
});

export type ManualOrderFormData = z.infer<typeof manualOrderSchema>;

// Login form schema
export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, { message: "Password is required" }),
});

export type LoginFormData = z.infer<typeof loginSchema>;

// Password reset schema
export const passwordResetSchema = z.object({
  email: emailSchema,
});

export type PasswordResetFormData = z.infer<typeof passwordResetSchema>;

// Order detail edit schema
export const orderDetailSchema = z.object({
  orderNumber: z.string().trim().max(VALIDATION.ORDER_NUMBER_MAX, { message: `Order number must be less than ${VALIDATION.ORDER_NUMBER_MAX} characters` }).optional().or(z.literal("")),
  address: z.string().trim().max(VALIDATION.ADDRESS_MAX, { message: `Address must be less than ${VALIDATION.ADDRESS_MAX} characters` }).optional().or(z.literal("")),
  totalPrice: z
    .string()
    .refine((val) => val === "" || !isNaN(parseFloat(val)), { message: "Price must be a valid number" })
    .refine((val) => val === "" || parseFloat(val) >= 0, { message: "Price cannot be negative" })
    .refine((val) => val === "" || parseFloat(val) <= 1000000, { message: "Price cannot exceed 1,000,000" })
    .optional()
    .or(z.literal("")),
  comments: z.string().trim().max(VALIDATION.COMMENTS_MAX, { message: `Comments must be less than ${VALIDATION.COMMENTS_MAX} characters` }).optional().or(z.literal("")),
  assignedTo: z.string().trim().max(VALIDATION.ASSIGNED_TO_MAX, { message: `Assigned person name must be less than ${VALIDATION.ASSIGNED_TO_MAX} characters` }).optional().or(z.literal("")),
  parsedItems: z.string().trim().max(VALIDATION.PARSED_ITEMS_MAX, { message: `Parsed items must be less than ${VALIDATION.PARSED_ITEMS_MAX} characters` }).optional().or(z.literal("")),
});

export type OrderDetailFormData = z.infer<typeof orderDetailSchema>;
